import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { resetPasswordWithCurrent } from '../services/api';

interface ChangePasswordLocationState {
  email?: string;
  tenantCode?: string;
}

const ChangePassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const state = (location.state || {}) as ChangePasswordLocationState;
  const searchParams = new URLSearchParams(window.location.search);
  const queryTenantCode = searchParams.get('tenantCode') || undefined;

  const [email, setEmail] = useState(state.email || '');
  const [tenantCode, setTenantCode] = useState(
    (state.tenantCode || queryTenantCode || '').toUpperCase()
  );
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 8,
    border: `2px solid ${isDark ? '#334155' : '#cbd5e1'}`,
    background: isDark ? '#020617' : 'white',
    color: isDark ? '#e5e7eb' : '#0f172a',
    fontSize: 17,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: 16,
    fontWeight: 600,
    color: isDark ? '#f1f5f9' : '#0f172a',
  };

  const validateEmail = (value: string): string => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Invalid email format';
    return '';
  };

  const validateTenantCode = (value: string): string => {
    if (!value) return 'Tenant code is required';
    if (!/^[A-Z0-9]+$/.test(value.toUpperCase())) return 'Tenant code must be letters and numbers only';
    return '';
  };

  const handleBackToLogin = () => {
    const codeUpper = tenantCode ? tenantCode.toUpperCase() : undefined;
    navigate('/login', {
      replace: true,
      state: { email, tenantCode: codeUpper, loginMethod: 'email' },
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    const codeError = validateTenantCode(tenantCode);
    if (codeError) {
      setError(codeError);
      return;
    }
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    try {
      setLoading(true);
      const codeUpper = tenantCode.toUpperCase();
      await resetPasswordWithCurrent(email.trim(), codeUpper, currentPassword, newPassword);
      setSuccess('Password updated successfully. Redirecting you to login...');
      setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { email: email.trim(), tenantCode: codeUpper, loginMethod: 'email' },
        });
      }, 1000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card" style={{ maxWidth: '500px' }}>
        <div className="login-header" style={{ textAlign: 'center', marginBottom: '15px' }}>
          <div
            style={{
              width: 70,
              height: 70,
              borderRadius: 20,
              margin: '0 auto 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, rgba(56,189,248,1) 0%, rgba(129,140,248,1) 50%, rgba(236,72,153,1) 100%)',
              boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
            }}
          >
            <span style={{ fontSize: 32 }}>🔐</span>
          </div>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              marginBottom: 4,
              color: isDark ? '#e5e7eb' : '#0f172a',
            }}
          >
            Change Password
          </h1>
          <p
            style={{
              fontSize: 14,
              color: isDark ? '#9ca3af' : '#64748b',
              maxWidth: 320,
              margin: '0 auto',
            }}
          >
            Enter your email, tenant code, current password, and a new password.
          </p>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${isDark ? '#f97373' : '#fecaca'}`,
              background: isDark ? 'rgba(153,27,27,0.2)' : '#fef2f2',
              color: isDark ? '#fecaca' : '#b91c1c',
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${isDark ? '#4ade80' : '#bbf7d0'}`,
              background: isDark ? 'rgba(22,163,74,0.2)' : '#ecfdf3',
              color: isDark ? '#bbf7d0' : '#166534',
              fontSize: 14,
            }}
          >
            {success}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 12 }}>
          <div>
            <label htmlFor="change-email" style={labelStyle}>Email</label>
            <input
              id="change-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              style={inputStyle}
            />
          </div>
          <div>
            <label htmlFor="change-tenant" style={labelStyle}>Tenant Code</label>
            <input
              id="change-tenant"
              type="text"
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="TENANT"
              style={{ ...inputStyle, textTransform: 'uppercase' }}
            />
          </div>
          <div>
            <label htmlFor="change-current" style={labelStyle}>Current Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="change-current"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current password"
                style={{ ...inputStyle, paddingRight: 48 }}
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: isDark ? '#64748b' : '#999',
                }}
              >
                {showCurrent ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="change-new" style={labelStyle}>New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="change-new"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                style={{ ...inputStyle, paddingRight: 48 }}
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: isDark ? '#64748b' : '#999',
                }}
              >
                {showNew ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="change-confirm" style={labelStyle}>Confirm New Password</label>
            <input
              id="change-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            type="button"
            onClick={handleBackToLogin}
            disabled={loading}
            style={{
              flex: 1.4,
              padding: '14px',
              borderRadius: 8,
              border: 'none',
              fontSize: 18,
              fontWeight: 600,
              background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Back to Login
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1.4,
              padding: '14px',
              borderRadius: 8,
              border: 'none',
              fontSize: 18,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)',
            }}
          >
            {loading ? 'Saving...' : 'Save New Password'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
